import { adminApi } from '@/api/client';
import { registerStepUpPrompt } from '@/lib/stepup';
import { Alert, App as AntdApp, Button, Input, Modal, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';

/**
 * 敏感运维操作的 step-up MFA 模态(全局挂载一次)。
 *
 * adminApi 撞到 STEP_UP_REQUIRED → 调注册的回调弹本框:
 *   - 未绑定 MFA → 先「绑定 MFA」(展示 secret / otpauth,加入 Authenticator)
 *   - 输入 6 位 TOTP → POST /auth/step-up → 拿到 step-up 令牌 → resolve,自动重试原操作
 */
type EnrollInfo = { secret: string; otpauth: string };

interface ErrDetails {
  response?: { data?: { error?: { details?: { mfa_not_enrolled?: boolean } } } };
}

export function StepUpModal() {
  const { message } = AntdApp.useApp();
  const [open, setOpen] = useState(false);
  const [totp, setTotp] = useState('');
  const [needEnroll, setNeedEnroll] = useState(false);
  const [enroll, setEnroll] = useState<EnrollInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((t: string | null) => void) | null>(null);

  useEffect(() => {
    registerStepUpPrompt(async () => {
      let mfaEnabled = false;
      try {
        const me = await adminApi.get<{ mfa_enabled?: boolean }>('/auth/me');
        mfaEnabled = !!me.data.mfa_enabled;
      } catch {
        /* /auth/me 失败按未绑定处理 */
      }
      setNeedEnroll(!mfaEnabled);
      setEnroll(null);
      setTotp('');
      setOpen(true);
      return new Promise<string | null>((resolve) => {
        resolver.current = resolve;
      });
    });
    return () => registerStepUpPrompt(null);
  }, []);

  const finish = (t: string | null) => {
    resolver.current?.(t);
    resolver.current = null;
    setOpen(false);
  };

  const doEnroll = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post<EnrollInfo>('/auth/mfa/enroll');
      setEnroll(r.data);
      setNeedEnroll(false);
    } catch {
      message.error('绑定 MFA 失败');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!/^\d{6}$/.test(totp)) {
      message.warning('请输入 6 位动态验证码');
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post<{ token: string }>('/auth/step-up', { totp });
      message.success('二次验证通过');
      finish(r.data.token);
    } catch (e) {
      const details = (e as ErrDetails)?.response?.data?.error?.details;
      if (details?.mfa_not_enrolled) {
        setNeedEnroll(true);
        message.warning('请先绑定 MFA');
      } else {
        message.error('验证码错误');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="敏感操作二次验证"
      onCancel={() => finish(null)}
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Alert type="info" showIcon message="该运维操作需要 TOTP 二次验证(step-up)。" />

        {needEnroll && !enroll && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>你还未绑定 MFA,请先绑定后再验证。</Typography.Text>
            <Button type="primary" loading={busy} onClick={doEnroll}>
              绑定 MFA
            </Button>
          </Space>
        )}

        {enroll && (
          <Alert
            type="warning"
            showIcon
            message="用 Authenticator 扫码或手动添加密钥,然后输入验证码"
            description={
              <Space direction="vertical">
                <Typography.Text copyable code>
                  {enroll.secret}
                </Typography.Text>
                <Typography.Link href={enroll.otpauth}>otpauth 链接</Typography.Link>
              </Space>
            }
          />
        )}

        {!needEnroll && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              value={totp}
              onChange={(e) => setTotp(e.target.value.trim())}
              placeholder="6 位动态验证码"
              maxLength={6}
              onPressEnter={() => void submit()}
              autoFocus
            />
            <Button type="primary" block loading={busy} onClick={() => void submit()}>
              验证并继续
            </Button>
          </Space>
        )}
      </Space>
    </Modal>
  );
}
